import {createApi} from "@reduxjs/toolkit/query/react";
import ErrorCode from "@/constants/error-codes";
import {apiPathName} from "@/lib/project-urls";
import {RootState} from "./store";
import {BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError,} from "@reduxjs/toolkit/query";
import {setCurrentUser} from "./user.reducer";
import {Mutex} from "async-mutex";
import {HYDRATE} from "next-redux-wrapper";
import {BackendResponseType, ErrorLevel} from "@/types/page-response";
import {FetchBaseQueryMeta} from "@reduxjs/toolkit/dist/query/fetchBaseQuery";
import {setError} from "./error.slice";
import {apiEndpoints} from "@/utils/api-endpoints";
import Cookies from "js-cookie";
import {forceLogout} from "@/utils/force-logout";

const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
    baseUrl: `${apiPathName}/`,
    prepareHeaders: (headers, {getState}) => {
        const token = Cookies.get("token")

        if (token) {
            headers.set("authorization", `Bearer ${token}`);
        }
        return headers;
    },
    credentials: "include",
});

const baseQueryWithBackendResponseFormat: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    {},
    FetchBaseQueryMeta
> = async (args, api, extraOptions) => {

    let result = await baseQuery(args, api, extraOptions);
    const url = typeof args === "string" ? args : args.url;

    if (result.error === undefined && result.data !== undefined) {
        const serverResult = result.data as BackendResponseType;

        if (serverResult.status === "success") {
            result.data = serverResult.data ?? null;

        } else {

            return {
                error: {
                    status: 500,
                    data: {
                        errorMessage: "فرمت پاسخ از سرور اشتباه است",
                        errorCode: ErrorCode.Exception,
                    },
                } as FetchBaseQueryError,
            };
        }
    } else if (result.error !== undefined && result.data === undefined) {
        const serverResult = result.error.data as BackendResponseType;
        const statusCode = result.error.status;

        if (url === apiEndpoints.User.RefreshToken.url) {
            forceLogout(api.dispatch);

            return result;
        }

        if (statusCode === 401) {
            return result;
        }

        const isError =
            !serverResult ||
            serverResult.status !== "success";

        const shouldIgnoreMessage =
            serverResult?.errorMessage?.includes("حساب مالی‌ مرتبط") ||
            serverResult?.errorMessage?.includes("شناسه کاربر ناشناس ارسالی");

        if (isError && !shouldIgnoreMessage) {
            api.dispatch(
                setError({
                    errorMessage: serverResult?.errorMessage || "خطا در انجام درخواست",
                    errorCode: serverResult?.errorCode,
                    errorLevel: serverResult?.errorLevel,
                })
            );
        } else if (statusCode === 500 || statusCode === 502 || statusCode === 503) {
            console.error("Server Internal Error:", serverResult?.errorMessage || result.error);
            api.dispatch(
                setError({
                    errorMessage:
                        "خطا در انجام عملیات. لطفاً بعداً دوباره تلاش کنید یا با پشتیبانی تماس بگیرید.",
                    errorCode: ErrorCode.Exception,
                    errorLevel: ErrorLevel.EXTERNAL,
                })
            );
        } else if (!serverResult) {
            api.dispatch(
                setError({
                    errorMessage: "اتصال به سرور برقرار نشد. لطفاً اتصال اینترنت خود را بررسی کنید.",
                    errorCode: ErrorCode.Exception,
                    errorLevel: 1,
                })
            );
        }

    }
    return result;
};

const baseQueryWithAuthCheck: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    {},
    FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
    const state = api.getState() as RootState;
    const currentUser = state.user.currentUser;

    const url = typeof args === "string" ? args : args.url;

    const publicEndpoints = [
        apiEndpoints.User.Login.url,
        apiEndpoints.News.GetAll.url,
        apiEndpoints.User.Register.url,
        apiEndpoints.User.RefreshToken.url,
        apiEndpoints.User.SendRegisterVerificationCode.url,
        apiEndpoints.User.SendForgotPasswordVerificationCode.url,
        apiEndpoints.User.CheckVerificationCode.url,
        apiEndpoints.User.ResetPassword.url,
        apiEndpoints.User.AddAnonymousUser.url,
        apiEndpoints.Payment.GetCardToCards.url,
    ];

    const isPublicEndpoint =
        publicEndpoints.includes(url) ||
        url.startsWith("/anonymous-users/") ||
        url.startsWith("/products/suggestions");


    if (!currentUser && !isPublicEndpoint) {
        return {
            error: {
                status: 401,
                data: {
                    errorMessage: "User not logged in",
                    errorCode: ErrorCode?.InvalidToken,
                },
            } as FetchBaseQueryError,
        };
    }

    return baseQueryWithReAuth(args, api, extraOptions);
};

const baseQueryWithReAuth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    {},
    FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
    await mutex.waitForUnlock();

    let result = await baseQueryWithBackendResponseFormat(args, api, extraOptions);

    if (result.data === undefined && result.error?.status === 401) {
        if (!mutex.isLocked()) {
            const release = await mutex.acquire();
            try {
                const refreshResult = await baseQueryWithBackendResponseFormat(
                    {
                        url: apiEndpoints.User.RefreshToken.url,
                        method: apiEndpoints.User.RefreshToken.method,
                    },
                    api,
                    extraOptions
                );

                const refreshData = refreshResult.data as { jwtToken?: string } | undefined;
                const jwt = refreshData?.jwtToken;


                if (jwt) {
                    const currentUser = (api.getState() as RootState).user.currentUser;

                    if (currentUser) {
                        api.dispatch(
                            setCurrentUser({
                                ...currentUser,
                                jwtToken: jwt,
                            })
                        );
                    }

                    result = await baseQueryWithBackendResponseFormat(
                        args,
                        api,
                        extraOptions
                    );
                } else {
                    forceLogout(api.dispatch);

                }
            } catch (err) {
                // forceLogout(api.dispatch);

            } finally {
                release();
            }
        } else {
            await mutex.waitForUnlock();
            result = await baseQueryWithBackendResponseFormat(args, api, extraOptions);
        }
    }
    return result;
};

export const api = createApi({
    reducerPath: "api",
    baseQuery: baseQueryWithAuthCheck,
    tagTypes: [
        "UsersPayment",
        "ProfilePicture",
        "User",
        "Cart",
        "ChatsList",
        "ChatMeta",
        "InventoryCounts",
        "Messages",
        "ProductGroup",
        "Products",
        "Order",
        "News",
        "Isaco",
        "Profile",
        "Finance",
        "CartCount",
        "Notifications",
        "Users",
        "ProductDetails",
        "Comments",
        "ProductPictures",
        "Faqs",
        "FinancialEvents",
        "Roles",
        "FactorChecks",
        "Factors",
        "FactorCheckItems",
    ],
    endpoints: () => ({}),
    extractRehydrationInfo(action, {reducerPath}) {
        if (action.type === HYDRATE) {
            return action.payload[reducerPath];
        }
    },
});